import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import { useCoverageRunTableRowActions } from '@/features/codeCoverage/pages/useCoverageRunTableRowActions';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { buildTagColorByName, resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { useTagOptions } from '@/shared/hooks/useTagOptions';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { type GridColumnVisibilityModel, type GridSortModel } from '@/ui/primitives';
import {
  DescriptionOutlinedIcon,
  EventOutlinedIcon,
  PersonOutlineOutlinedIcon,
} from '@/ui/primitives/icons';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type {
  CoverageRunDto,
  MetricThreshold,
  QueryResult,
  QueryResult as GridQueryResult,
} from '@flowqa/shared';

type CoverageRunsTableProps = {
  runs: CoverageRunDto[];
  queryKey: QueryKey;
  projectId?: string | null;
  initialData?: GridQueryResult<CoverageRunDto>;
  loading?: boolean;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
};

const COVERAGE_ROW_ESTIMATE_HEIGHT = 72;
const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;

const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }
  return `${String(covered)} / ${String(total)}`;
};

const buildMetricColumn = (
  field: string,
  headerName: string,
  thresholdKey: CoverageMetricThresholdKey,
  resolvePercent: (row: CoverageRunDto) => number | null | undefined,
  resolveCovered: (row: CoverageRunDto) => number | null | undefined,
  resolveTotal: (row: CoverageRunDto) => number | null | undefined,
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold,
): GridColDef<CoverageRunDto> => ({
  field,
  headerName,
  flex: 0.55,
  sortable: false,
  filterable: false,
  align: 'left',
  headerAlign: 'left',
  cellClassName: 'table-metric-cell',
  renderCell: (params) => (
    <TableCellContainer gap="none">
      <CoverageMetricTableCell
        primary={formatPercent(resolvePercent(params.row))}
        secondary={formatCountPair(resolveCovered(params.row), resolveTotal(params.row))}
        primaryColor={resolveCoverageMetricThresholdColor(
          resolvePercent(params.row),
          resolveThreshold(thresholdKey),
        )}
      />
    </TableCellContainer>
  ),
});

const compareRunStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareRunNumbers = (
  leftValue: number | null | undefined,
  rightValue: number | null | undefined,
): number => (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const compareRunDates = (
  leftValue: Date | string | null | undefined,
  rightValue: Date | string | null | undefined,
): number => new Date(leftValue ?? 0).getTime() - new Date(rightValue ?? 0).getTime();

const sortRuns = (runs: CoverageRunDto[], input: GridQueryInput): CoverageRunDto[] => {
  const sort = input.sorts[0] ?? { field: 'createdAt', direction: 'desc' as const };
  const directionFactor = sort.direction === 'asc' ? 1 : -1;
  return [...runs].sort((left, right) => {
    let result = 0;
    switch (sort.field) {
      case 'projectName':
        result = compareRunStrings(left.projectName, right.projectName);
        break;
      case 'linesPct':
        result = compareRunNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareRunNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareRunNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareRunNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'createdAt':
      default:
        result = compareRunDates(
          left.capturedAt ?? left.createdAt,
          right.capturedAt ?? right.createdAt,
        );
        break;
    }

    if (result !== 0) {
      return result * directionFactor;
    }

    return compareRunDates(left.createdAt, right.createdAt) * -1;
  });
};

export const CoverageRunsTable = ({
  runs,
  queryKey,
  projectId = null,
  initialData,
  loading = false,
  initialSortModel,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: CoverageRunsTableProps): ReactElement => {
  const { thresholds } = useProjectMetricThresholds(projectId);
  const { tagOptions } = useTagOptions({ projectId, enabled: runs.length > 0 });
  const [editTarget, setEditTarget] = useState<CoverageRunDto | null>(null);
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const resolveThreshold = useCallback(
    (metricKey: CoverageMetricThresholdKey) => thresholds.coverageRuns[metricKey],
    [thresholds.coverageRuns],
  );
  const {
    deleteLoading,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useCoverageRunTableRowActions();
  const columns = useMemo<Array<GridColDef<CoverageRunDto>>>(
    () => [
      {
        field: 'run',
        headerName: getDetailLabel('run'),
        flex: 0.9,
        sortable: false,
        cellClassName: 'table-name-cell',
        renderCell: (params) => {
          const primaryLabel =
            params.row.commitSha?.slice(0, 8) ??
            params.row.buildId ??
            params.row.pipelineExecutionId ??
            params.row.id.slice(0, 8);
          const tags = resolveEntityTags(params.row).map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, tagColorByName),
          }));
          const secondaryParts = [
            params.row.projectName?.trim() ?? 'Coverage Run',
            params.row.branch?.trim() ?? null,
          ].filter((value): value is string => value !== null && value.length > 0);

          return (
            <TableCellContainer gap="none">
              <TableNameCell
                primary={{
                  label: primaryLabel,
                  href: `/coverage-runs/${params.row.id}`,
                  sx: { fontFamily: (theme) => theme.typography.mono.fontFamily },
                }}
                secondary={{ label: secondaryParts.join(' · ') }}
                tags={tags}
              />
            </TableCellContainer>
          );
        },
      },
      buildMetricColumn(
        'lineCoverage',
        'Line',
        'line',
        (row) => row.linesPct,
        (row) => row.linesCovered,
        (row) => row.linesTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'functionCoverage',
        'Function',
        'function',
        (row) => row.functionsPct,
        (row) => row.functionsCovered,
        (row) => row.functionsTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'branchCoverage',
        'Branch',
        'branch',
        (row) => row.branchesPct,
        (row) => row.branchesCovered,
        (row) => row.branchesTotal,
        resolveThreshold,
      ),
      buildMetricColumn(
        'statementCoverage',
        'Statement',
        'statement',
        (row) => row.statementsPct,
        (row) => row.statementsCovered,
        (row) => row.statementsTotal,
        resolveThreshold,
      ),
      {
        field: 'details',
        headerName: getDetailLabel('details'),
        flex: 0.95,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'table-meta-details-cell',
        renderCell: (params) => {
          const capturedAt = params.row.capturedAt ?? params.row.createdAt;
          const capturedLabel = formatRelativeTime(capturedAt) || '-';
          const capturedTitle = formatDate(capturedAt, { includeTime: true });

          return (
            <TableCellContainer gap="none">
              <TableBreakdownStack>
                <TableBreakdownLine
                  rowTitle="Upload"
                  icon={
                    <DescriptionOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                  }
                  content={params.row.uploadFileName ?? `Upload ${params.row.uploadId.slice(0, 8)}`}
                  disableLinks
                  contentSx={{ fontFamily: (theme) => theme.typography.mono.fontFamily }}
                />
                <TableBreakdownLine
                  rowTitle="Captured"
                  icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
                  content={capturedLabel}
                  valueTitle={capturedTitle}
                  disableLinks
                  contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
                  valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
                />
                <TableBreakdownLine
                  rowTitle="Uploaded By"
                  icon={
                    <PersonOutlineOutlinedIcon
                      sx={{ fontSize: (theme) => theme.app.iconSizes.sm }}
                    />
                  }
                  content={params.row.createdByEmail ?? params.row.createdByName ?? '-'}
                  disableLinks
                />
              </TableBreakdownStack>
            </TableCellContainer>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        flex: 0.1,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<CoverageRunDto>) => (
          <TableActionsCell
            rowName={params.row.commitSha?.slice(0, 8) ?? params.row.id.slice(0, 8)}
            editActionLabel={getEntityActionLabel('edit', 'Coverage Run')}
            onEdit={() => {
              setEditTarget(params.row);
            }}
            disableEdit={updateRowTagsColorPendingId === params.row.id}
            showEditTagsAction={false}
            deleteActionLabel={getEntityActionLabel('delete', 'Coverage Run')}
            onDelete={() => {
              onDeleteRow(params.row);
            }}
          />
        ),
      },
    ],
    [onDeleteRow, resolveThreshold, updateRowTagsColorPendingId],
  );

  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CoverageRunDto>> => {
      const sortedRuns = sortRuns(runs, input);
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;
      return await Promise.resolve({
        items: sortedRuns.slice(startIndex, startIndex + pageSize),
        total: sortedRuns.length,
        page,
        pageSize,
      });
    },
    [runs],
  );

  const gridProps = buildTableGridProps<CoverageRunDto>({
    queryFn,
    queryKey,
    pageSizeOptions: [5, 10, 25, 50],
    initialPageSize: 5,
    noRowsText: getEmptyStateMessage('entity.noneFound', { entity: 'coverageRun' }),
  });

  return (
    <>
      <ServerDataTable<CoverageRunDto>
        queryPath="coverage-runs/local-table"
        initialData={initialData}
        persistKey={null}
        syncStateToUrl={false}
        fixedFilters={[]}
        initialSortModel={initialSortModel}
        externalLoading={loading}
        {...gridProps}
        columns={columns}
        getRowId={(row) => row.id}
        search=""
        columnVisibilityModel={columnVisibilityModel}
        onQueryMeta={undefined}
        disableColumnMenu
        disableColumnSorting
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        autoHeight
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="run"], & .MuiDataGrid-cell[data-field="lineCoverage"], & .MuiDataGrid-cell[data-field="functionCoverage"], & .MuiDataGrid-cell[data-field="branchCoverage"], & .MuiDataGrid-cell[data-field="statementCoverage"], & .MuiDataGrid-cell[data-field="details"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
            '& .MuiDataGrid-columnHeaders': {
              minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
            },
          },
        ]}
        gridProps={{
          getEstimatedRowHeight: () => COVERAGE_ROW_ESTIMATE_HEIGHT,
          onColumnVisibilityModelChange,
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'coverageRun' })}
        description="This coverage run and its associated directory and file coverage will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'coverageRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Coverage Run"
        entityName={editTarget?.commitSha?.slice(0, 8) ?? editTarget?.id.slice(0, 8) ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.id ?? null)}
        cascadeCopy="Tags will be applied to this coverage run and reflected across associated coverage views."
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.id ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({ row: editTarget, tags, colors });
          setEditTarget(null);
        }}
      />
    </>
  );
};
